import { feature, featuresProduct, flattenFeatures } from "@protontech/autofill/features/feature";
import { fieldAttrs, fieldAutocomplete, fieldAutocompleteOff, fieldCC, fieldExotic, fieldIdentity, fieldLabel, fieldLogin, fieldNext, fieldPrev, fieldRegister, fieldText } from "@protontech/autofill/features/v1/abstract.field";
import { any } from "@protontech/autofill/utils/combinators";
import { getAllFieldHaystacks } from "@protontech/autofill/utils/extract";
import {
  matchPasswordConfirm,
  matchPasswordConfirmAttr,
  matchPasswordCreate,
  matchPasswordCreateAttr,
  matchPasswordCurrent,
  matchPasswordCurrentAttr,
  matchPasswordOutlier,
  matchPasswordReset,
  matchPasswordResetAttr,
} from "@protontech/autofill/utils/re";

/* OUTLIERS */
export const passwordOutlier = feature("passwordOutlier", { fieldAttrs, fieldText, fieldLabel }, (p) => any(matchPasswordOutlier)(p.fieldAttrs.concat(p.fieldLabel, p.fieldText)));
export const outlier = feature("outlier", { fieldCC, fieldIdentity, passwordOutlier }, (p) => p.fieldCC || p.fieldIdentity || p.passwordOutlier);

/* AUTOCOMPLETE (gated on outlier for new/current/OTP) */
export const autocompleteGated = feature("autocompleteGated", { fieldAutocomplete, outlier }, (p) => (p.outlier ? "" : (p.fieldAutocomplete ?? "")));
export const autocompleteNew = feature("autocompleteNew", { autocompleteGated }, (p) => p.autocompleteGated === "new-password");
export const autocompleteCurrent = feature("autocompleteCurrent", { autocompleteGated }, (p) => p.autocompleteGated === "current-password");
export const autocompleteOTP = feature("autocompleteOTP", { autocompleteGated }, (p) => p.autocompleteGated.includes("one-time-code"));

/* CURRENT PASSWORD HAYSTACKS */
export const attrCurrent = feature("attrCurrent", { fieldAttrs }, (p) => any(matchPasswordCurrentAttr)(p.fieldAttrs));
export const textCurrent = feature("textCurrent", { fieldText }, (p) => matchPasswordCurrent(p.fieldText));
export const labelCurrent = feature("labelCurrent", { fieldLabel }, (p) => matchPasswordCurrent(p.fieldLabel));
export const current = feature("current", { attrCurrent, textCurrent, labelCurrent }, (p) => p.attrCurrent || p.textCurrent || p.labelCurrent);

/* NEW PASSWORD HAYSTACKS */
export const attrCreate = feature("attrCreate", { fieldAttrs }, (p) => any(matchPasswordCreateAttr)(p.fieldAttrs));
export const textCreate = feature("textCreate", { fieldText }, (p) => matchPasswordCreate(p.fieldText));
export const labelCreate = feature("labelCreate", { fieldLabel }, (p) => matchPasswordCreate(p.fieldLabel));
export const create = feature("create", { attrCreate, textCreate, labelCreate }, (p) => p.attrCreate || p.textCreate || p.labelCreate);

/* CONFIRM PASSWORD HAYSTACKS */
export const attrConfirm = feature("attrConfirm", { fieldAttrs }, (p) => any(matchPasswordConfirmAttr)(p.fieldAttrs));
export const textConfirm = feature("textConfirm", { fieldText }, (p) => matchPasswordConfirm(p.fieldText));
export const labelConfirm = feature("labelConfirm", { fieldLabel }, (p) => matchPasswordConfirm(p.fieldLabel));
export const confirm = feature("confirm", { attrConfirm, textConfirm, labelConfirm }, (p) => p.attrConfirm || p.textConfirm || p.labelConfirm);

/* RESET PASSWORD HAYSTACKS */
export const attrReset = feature("attrReset", { fieldAttrs }, (p) => any(matchPasswordResetAttr)(p.fieldAttrs));
export const textReset = feature("textReset", { fieldText }, (p) => matchPasswordReset(p.fieldText));
export const labelReset = feature("labelReset", { fieldLabel }, (p) => matchPasswordReset(p.fieldLabel));
export const reset = feature("reset", { attrReset, textReset, labelReset }, (p) => p.attrReset || p.textReset || p.labelReset);

/* INTENT SIGNALS */
export const maybeCurrent = feature("maybeCurrent", { current }, (p) => p.current);
export const maybeNew = feature("maybeNew", { create, confirm, reset }, (p) => p.create || p.confirm || p.reset);

/** PREV PW FIELD */
export const prevPwFieldEl = feature("prevPwFieldEl", { fieldPrev }, (p) => (p.fieldPrev?.getAttribute("type") === "password" ? (p.fieldPrev as HTMLInputElement) : null));
export const prevPwField = feature("prevPwField", { prevPwFieldEl }, (p) => p.prevPwFieldEl !== null);
export const prevPwHaystack = feature("prevPwHaystack", { prevPwFieldEl }, (p) => (p.prevPwFieldEl ? getAllFieldHaystacks(p.prevPwFieldEl) : []));
export const prevPwAutocomplete = feature("prevPwAutocomplete", { prevPwFieldEl }, (p) => p.prevPwFieldEl?.getAttribute("autocomplete") ?? null);
export const prevPwNew = feature("prevPwNew", { prevPwAutocomplete, prevPwHaystack }, (p) => p.prevPwAutocomplete === "new-password" || any(matchPasswordCreate)(p.prevPwHaystack));
export const prevPwCurrent = feature("prevPwCurrent", { prevPwAutocomplete, prevPwHaystack }, (p) => p.prevPwAutocomplete === "current-password" || any(matchPasswordCurrent)(p.prevPwHaystack));

/** NEXT PW FIELD */
export const nextPwFieldEl = feature("nextPwFieldEl", { fieldNext }, (p) => (p.fieldNext?.getAttribute("type") === "password" ? (p.fieldNext as HTMLInputElement) : null));
export const nextPwField = feature("nextPwField", { nextPwFieldEl }, (p) => p.nextPwFieldEl !== null);
export const nextPwHaystack = feature("nextPwHaystack", { nextPwFieldEl }, (p) => (p.nextPwFieldEl ? getAllFieldHaystacks(p.nextPwFieldEl) : []));
export const nextPwAutocomplete = feature("nextPwAutocomplete", { nextPwFieldEl }, (p) => p.nextPwFieldEl?.getAttribute("autocomplete") ?? null);
export const nextPwNew = feature("nextPwNew", { nextPwAutocomplete, nextPwHaystack }, (p) => p.nextPwAutocomplete === "new-password" || any(matchPasswordCreate)(p.nextPwHaystack));
export const nextPwConfirm = feature("nextPwConfirm", { nextPwHaystack }, (p) => any(matchPasswordConfirm)(p.nextPwHaystack));
export const nextPwCurrent = feature("nextPwCurrent", { nextPwAutocomplete, nextPwHaystack }, (p) => p.nextPwAutocomplete === "current-password" || any(matchPasswordCurrent)(p.nextPwHaystack));

export const fieldLogin_autocompleteNew = featuresProduct(fieldLogin, autocompleteNew);
export const fieldLogin_maybeNew = featuresProduct(fieldLogin, maybeNew);
export const fieldRegister_autocompleteCurrent = featuresProduct(fieldRegister, autocompleteCurrent);
export const fieldRegister_maybeCurrent = featuresProduct(fieldRegister, maybeCurrent);
export const prevPwCurrent_nextPwNew = featuresProduct(prevPwCurrent, nextPwNew);

export const passwordFeatures = flattenFeatures({
  attrConfirm,
  attrCreate,
  attrCurrent,
  attrReset,
  autocompleteCurrent,
  autocompleteNew,
  autocompleteOTP,
  fieldAutocompleteOff,
  fieldCC,
  fieldExotic,
  fieldIdentity,
  fieldLogin,
  fieldRegister,
  labelConfirm,
  labelCreate,
  labelCurrent,
  nextPwConfirm,
  nextPwCurrent,
  nextPwField,
  nextPwNew,
  passwordOutlier,
  prevPwCurrent,
  prevPwField,
  prevPwNew,
  textConfirm,
  textCreate,
  textCurrent,

  fieldLogin_autocompleteNew,
  fieldLogin_maybeNew,
  fieldRegister_autocompleteCurrent,
  fieldRegister_maybeCurrent,
  prevPwCurrent_nextPwNew,
});
